// The Google tag in public/index.html owns initialization. All application
// events use its queue, including events sent before the remote script loads.
export const ensureAnalyticsTransport = () => {
    if (typeof window === 'undefined') return null;
    window.dataLayer = window.dataLayer || [];
    if (typeof window.gtag !== 'function') {
        window.gtag = function () { window.dataLayer.push(arguments); };
    }
    return window.gtag;
};

export const CAMPAIGN_FIELDS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
const CAMPAIGN_STORAGE_KEY = 'prepbox_campaign';
const safeToken = (value) => typeof value === 'string'
    && /^[a-z0-9][a-z0-9_.-]{0,99}$/i.test(value)
    && !/\d{7,}/.test(value);
const safeText = (value) => typeof value === 'string' && value.length <= 100
    && !/[@\r\n]|\d[\d ()+.-]{6,}\d/.test(value);
const safeId = (value) => (typeof value === 'number' && Number.isSafeInteger(value) && value >= 0)
    || (typeof value === 'string' && /^\d{1,10}$/.test(value));
const safeBookId = (value) => safeId(value) || safeToken(value)
    || (typeof value === 'string' && /^[a-f0-9]{4,8}(?:-[a-f0-9]{4}){3}-[a-f0-9]{12,16}$/i.test(value));

export const campaignFromSearch = (search = '') => {
    const params = new URLSearchParams(search);
    return CAMPAIGN_FIELDS.reduce((campaign, key) => {
        const value = params.get(key);
        if (safeToken(value)) campaign[key] = value;
        return campaign;
    }, {});
};

// Keep a landing campaign within this tab when internal routing removes its
// query string. Only these marketing fields are persisted or forwarded.
export const rememberCampaign = (search = '') => {
    const params = new URLSearchParams(search);
    const campaign = campaignFromSearch(search);
    try {
        if (CAMPAIGN_FIELDS.some((key) => params.has(key))) {
            window.sessionStorage.setItem(CAMPAIGN_STORAGE_KEY, JSON.stringify(campaign));
            return campaign;
        }
        const stored = JSON.parse(window.sessionStorage.getItem(CAMPAIGN_STORAGE_KEY) || '{}');
        return campaignFromSearch(new URLSearchParams(stored).toString());
    } catch (_) {
        return campaign;
    }
};

export const withCampaign = (href, search = window.location.search) => {
    const url = new URL(href, window.location.origin);
    const campaign = rememberCampaign(search);
    Object.entries(campaign).forEach(([key, value]) => url.searchParams.set(key, value));
    return url.href;
};

export const safeAnalyticsUrl = (value, includeCampaign = false) => {
    if (!value) return '';
    try {
        const url = new URL(value, window.location.origin);
        if (!['http:', 'https:'].includes(url.protocol)) return '';
        const decodedPath = decodeURIComponent(url.pathname);
        const pathname = /[@\r\n]|\d{7,}/.test(decodedPath) ? '/redacted' : url.pathname;
        const query = includeCampaign ? new URLSearchParams(campaignFromSearch(url.search)).toString() : '';
        return `${url.origin}${pathname}${query ? `?${query}` : ''}`;
    } catch (_) {
        return '';
    }
};

// Explicit content metadata only: never forward form values, arbitrary event
// payloads, search queries, or unfiltered URLs to Analytics.
const PARAMETER_RULES = {
    // Workbooks use catalog tokens/UUIDs as well as legacy numeric IDs.
    book_id: safeBookId,
    course_id: safeId,
    topic_id: safeId,
    book_name: safeText,
    slug: safeToken,
    course_slug: safeToken,
    article_slug: safeToken,
    article_locale: (value) => ['en', 'ko'].includes(value),
    user_type: (value) => ['Parent', 'Teacher', 'Student'].includes(value),
    available: (value) => typeof value === 'boolean',
    lesson_type: (value) => ['interactive', 'video'].includes(value),
    placement: (value) => ['topic_row', 'interactive_badge', 'video_badge', 'selected_pathway', 'studio_band', 'studio_chapter', 'studio_pill'].includes(value),
    video_percent: (value) => [10, 25, 50, 75, 100].includes(value),
    share_method: (value) => ['copy_link', 'copy_prompt', 'native_share'].includes(value),
    cta_location: (value) => ['article_end'].includes(value),
    failure_reason: (value) => ['invalid_file', 'browser_error'].includes(value),
};

export const safeEventParams = (params = {}) => Object.entries(params).reduce((safe, [key, value]) => {
    const rule = Object.prototype.hasOwnProperty.call(PARAMETER_RULES, key) && PARAMETER_RULES[key];
    if (rule && rule(value)) safe[key] = value;
    if (key === 'page_url') safe.page_url = safeAnalyticsUrl(value);
    return safe;
}, {});

export const sendAnalyticsEvent = (name, params = {}) => {
    if (!/^[a-z][a-z0-9_]{0,39}$/.test(name)) return;
    const gtag = ensureAnalyticsTransport();
    if (!gtag) return;
    gtag('event', name, {
        ...safeEventParams(params),
        page_location: safeAnalyticsUrl(window.location.href, true),
    });
};

export const sendAnalyticsPageView = ({ path, search = '', referrer = '', title } = {}) => {
    const gtag = ensureAnalyticsTransport();
    if (!gtag) return;
    const page = {
        page_location: safeAnalyticsUrl(`${window.location.origin}${path || window.location.pathname}${search}`, true),
        page_referrer: safeAnalyticsUrl(referrer),
        page_title: safeText(title) ? title : 'PrepBox',
    };
    // Keep automatically collected events on the current virtual page too.
    gtag('set', page);
    gtag('event', 'page_view', page);
};
